
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { 
  Loader2, RefreshCw, CheckCircle, XCircle, 
  Clock, AlertCircle, Plus, FilePlus, X, Play
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import CreateTaskModal from '../modals/CreateTaskModal';

// Define Task interface
interface Task {
  id: string;
  type: string;
  status: string;
  created_at: string;
  updated_at: string;
  slug: string;
  version?: string;
  quarter?: string;
  force?: boolean;
  result?: any;
  params?: any;
  dependencies?: string[];
}

const TaskManager: React.FC = () => {
  const { toast } = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalTasks, setTotalTasks] = useState(0);
  
  // Filters - Default to only show pending and running tasks
  const [statusFilters, setStatusFilters] = useState<string[]>(['pending', 'running']);
  const [typeFilter, setTypeFilter] = useState('all');
  const [slugFilter, setSlugFilter] = useState('');
  const [createdFilter, setCreatedFilter] = useState('7days');
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null);
  const [cancellingTaskIds, setCancellingTaskIds] = useState<string[]>([]);
  const [forcingTaskIds, setForcingTaskIds] = useState<string[]>([]);

  useEffect(() => {
    fetchTasks();

    // Set up auto-refresh if enabled
    if (refreshInterval) {
      const intervalId = setInterval(fetchTasks, refreshInterval * 1000);
      return () => clearInterval(intervalId);
    }
  }, [page, pageSize, statusFilters, typeFilter, slugFilter, createdFilter, refreshInterval]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      // Build the query
      let query = supabase
        .from('tasks')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);
      
      // Apply filters
      if (statusFilters.length > 0 && !statusFilters.includes('all')) {
        query = query.in('status', statusFilters);
      }
      
      if (typeFilter !== 'all') {
        query = query.eq('type', typeFilter);
      }
      
      if (slugFilter) {
        query = query.ilike('slug', `%${slugFilter}%`);
      }

      // Apply created date filter
      if (createdFilter !== 'all') {
        const filterDate = new Date();
        filterDate.setHours(0, 0, 0, 0);
        if (createdFilter === '7days') {
          filterDate.setDate(filterDate.getDate() - 7);
        }
        query = query.gte('created_at', filterDate.toISOString());
      }

      const { data, error, count } = await query;
      
      if (error) {
        throw error;
      }
      
      setTasks(data as Task[] || []);
      setTotalTasks(count || 0);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch tasks',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="flex items-center gap-1"><Clock className="h-3 w-3" /> Pending</Badge>;
      case 'running':
        return <Badge variant="secondary" className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Running</Badge>;
      case 'done':
        return <Badge variant="success" className="flex items-center gap-1 bg-green-100 text-green-800"><CheckCircle className="h-3 w-3" /> Done</Badge>;
      case 'failed':
        return <Badge variant="destructive" className="flex items-center gap-1"><XCircle className="h-3 w-3" /> Failed</Badge>;
      case 'timed_out':
        return <Badge variant="destructive" className="flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Timed Out</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="flex items-center gap-1 bg-gray-100 text-gray-800"><XCircle className="h-3 w-3" /> Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleRefreshChange = (value: string) => {
    const interval = value === 'off' ? null : parseInt(value);
    setRefreshInterval(interval);
  };

  const cancelTask = async (taskId: string) => {
    setCancellingTaskIds(prev => [...prev, taskId]);
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ status: 'cancelled' })
        .eq('id', taskId);
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Success',
        description: 'Task cancelled successfully',
      });
      
      // Refresh tasks to show updated status
      fetchTasks();
    } catch (error) {
      console.error('Error cancelling task:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel task',
        variant: 'destructive',
      });
    } finally {
      setCancellingTaskIds(prev => prev.filter(id => id !== taskId));
    }
  };

  const forceExecuteTask = async (taskId: string) => {
    setForcingTaskIds(prev => [...prev, taskId]);
    try {
      const { data, error } = await supabase.functions.invoke('task-dispatcher', {
        method: 'POST',
        body: {
          action: 'force_execute',
          task_id: taskId
        }
      });
      
      if (error) {
        throw error;
      }
      
      if (!data.success) {
        throw new Error(data.error || 'Force execution failed');
      }
      
      toast({
        title: 'Success',
        description: 'Task force executed successfully',
      });
      
      // Refresh tasks to show updated status
      fetchTasks();
    } catch (error) {
      console.error('Error force executing task:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to force execute task',
        variant: 'destructive',
      });
    } finally {
      setForcingTaskIds(prev => prev.filter(id => id !== taskId));
    }
  };

  return (
    <div className="space-y-6">
      {/* Create New Task Button - Right Aligned */}
      <div className="flex justify-end mb-4">
        <Button 
          className="gap-2"
          onClick={() => setShowCreateModal(true)}
        >
          <Plus className="h-4 w-4" />
          Create New Task
        </Button>
      </div>
      
      {/* Filters Card with Total Tasks */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Task Filters</CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Total:</span>
                <span className="font-medium">{totalTasks}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Auto Refresh:</span>
                <Select 
                  value={refreshInterval?.toString() || "0"} 
                  onValueChange={handleRefreshChange}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue placeholder="Off" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Off</SelectItem>
                    <SelectItem value="5">5s</SelectItem>
                    <SelectItem value="10">10s</SelectItem>
                    <SelectItem value="30">30s</SelectItem>
                    <SelectItem value="60">1m</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Type Filter */}
            <div>
              <Label htmlFor="typeFilter" className="text-xs">Task Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="EXTRACT_METRICS">Extract Metrics</SelectItem>
                  <SelectItem value="EXTRACT_TAGS">Extract Tags</SelectItem>
                  <SelectItem value="SCREENSHOT_PAGE">Screenshot Page</SelectItem>
                  <SelectItem value="PROCESS_METRIC_EVENTS">Process Metrics</SelectItem>
                  <SelectItem value="VERIFY_IMAGE">Verify Image</SelectItem>
                  <SelectItem value="TAG_PAGE">Tag Page</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Slug Filter */}
            <div>
              <Label htmlFor="slugFilter" className="text-xs">Company Slug</Label>
              <Input
                id="slugFilter"
                placeholder="Filter by slug"
                value={slugFilter}
                onChange={(e) => setSlugFilter(e.target.value)}
              />
            </div>

            {/* Status Multi-Select */}
            <div>
              <Label htmlFor="statusFilter" className="text-xs">Status</Label>
              <Select
                value={statusFilters.length === 0 ? "all" : statusFilters.join(",")}
                onValueChange={(value) => {
                  if (value === "all") {
                    setStatusFilters([]);
                  } else {
                    setStatusFilters(value.split(","));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="timed_out">Timed Out</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="pending,running">Pending & Running</SelectItem>
                  <SelectItem value="done,failed">Done & Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Created Date Filter */}
            <div>
              <Label htmlFor="createdFilter" className="text-xs">Created</Label>
              <Select value={createdFilter} onValueChange={setCreatedFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Select date" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  <SelectItem value="7days">Last 7 days</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Task List</CardTitle>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const { data, error } = await supabase.functions.invoke('task-dispatcher', {
                    method: 'POST',
                    body: { action: 'process_next' }
                  });
                  
                  if (error) throw error;
                  toast({
                    title: 'Success',
                    description: 'Processed next task in queue',
                  });
                  fetchTasks();
                } catch (error) {
                  console.error('Error processing next task:', error);
                  toast({
                    title: 'Error',
                    description: 'Failed to process next task',
                    variant: 'destructive',
                  });
                }
              }}
            >
              Process Next
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              onClick={async () => {
                if (confirm('Are you sure you want to clear the task queue?')) {
                  try {
                    const { data, error } = await supabase.functions.invoke('clear-queue', {
                      method: 'POST'
                    });
                    
                    if (error) throw error;
                    toast({
                      title: 'Success',
                      description: 'Task queue cleared',
                    });
                    fetchTasks();
                  } catch (error) {
                    console.error('Error clearing task queue:', error);
                    toast({
                      title: 'Error',
                      description: 'Failed to clear task queue',
                      variant: 'destructive',
                    });
                  }
                }
              }}
            >
              Clear Queue
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-8 h-8 p-0"
              onClick={fetchTasks}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted text-left">
                    <th className="p-2 font-medium">ID</th>
                    <th className="p-2 font-medium">Type</th>
                    <th className="p-2 font-medium">Slug</th>
                    <th className="p-2 font-medium">Status</th>
                    <th className="p-2 font-medium">Created</th>
                    <th className="p-2 font-medium">Updated</th>
                    <th className="p-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        <span className="block mt-2 text-sm text-muted-foreground">
                          Loading tasks...
                        </span>
                      </td>
                    </tr>
                  ) : tasks.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center">
                        <div className="text-muted-foreground flex flex-col items-center">
                          <FilePlus className="h-8 w-8 mb-2 opacity-40" />
                          <p>No tasks found</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-2"
                            onClick={() => setShowCreateModal(true)}
                          >
                            Create a task
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    tasks.map((task) => (
                      <tr key={task.id} className="border-t hover:bg-muted/50">
                        <td className="p-2 font-mono text-xs">{task.id.substring(0, 8)}...</td>
                        <td className="p-2">{task.type}</td>
                        <td className="p-2">{task.slug}</td>
                        <td className="p-2">{getStatusBadge(task.status)}</td>
                        <td className="p-2 whitespace-nowrap">{formatDate(task.created_at)}</td>
                        <td className="p-2 whitespace-nowrap">{formatDate(task.updated_at)}</td>
                        <td className="p-2">
                          <div className="flex gap-1">
                            {task.status === 'pending' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => forceExecuteTask(task.id)}
                                  disabled={forcingTaskIds.includes(task.id)}
                                >
                                  {forcingTaskIds.includes(task.id) ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <>
                                      <Play className="h-3 w-3 mr-1" />
                                      Force Execute
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => cancelTask(task.id)}
                                  disabled={cancellingTaskIds.includes(task.id)}
                                >
                                  {cancellingTaskIds.includes(task.id) ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <>
                                      <X className="h-3 w-3 mr-1" />
                                      Cancel
                                    </>
                                  )}
                                </Button>
                              </>
                            )}
                            {task.status === 'running' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => cancelTask(task.id)}
                                disabled={cancellingTaskIds.includes(task.id)}
                              >
                                {cancellingTaskIds.includes(task.id) ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <>
                                    <X className="h-3 w-3 mr-1" />
                                    Cancel
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center space-x-2">
              <Label htmlFor="pageSize" className="text-sm">Page size:</Label>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => {
                  setPageSize(parseInt(value));
                  setPage(0);
                }}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue placeholder="Page size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <span className="text-sm">
                Page {page + 1} of {Math.max(1, Math.ceil(totalTasks / pageSize))}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(Math.ceil(totalTasks / pageSize) - 1, page + 1))}
                disabled={page >= Math.ceil(totalTasks / pageSize) - 1}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchTasks}
      />
    </div>
  );
};

export default TaskManager;
